/**
 * lib/api.ts — every request this app makes to our own server.
 *
 * One module, so that the bearer token is attached in one place and a refusal is shaped in
 * one place. Nothing here knows about React.
 *
 * ## A refusal is a value, not an exception
 *
 * Every call returns a discriminated result. The two things the interface has to do with a
 * failure — show the «subscribe» screen for `NOT_SUBSCRIBED`, show a sentence for anything
 * else — are both decisions about a *value*, and `try`/`catch` around every call site is how
 * one of them eventually gets forgotten.
 *
 * The one exception is a network failure, which is also turned into a value: on a phone,
 * «нет сети» is an ordinary state, not an error.
 *
 * ## A pass that ran out is renewed once, here (task 43)
 *
 * The session pass lives six hours. A `401` to a request that carried one is handed to the
 * handler `store/session.ts` registers ({@link onUnauthorized}): it signs in again with the
 * launch data still in the page and answers the new pass, and the request goes once more with
 * it. A second `401` is the caller's, as before. One place, so that every request of the app —
 * the queue's save, a fresh sound link, a heart — is covered by being a request, not by
 * remembering to be.
 */

/** The envelope `lib/errors.ts` sends on the server side. */
export interface ApiFailure {
  readonly code: string;
  readonly message: string;
  readonly details?: Record<string, unknown> | undefined;
  /** HTTP status, or 0 when the request never reached the server. */
  readonly status: number;
}

export type ApiResult<T> =
  { readonly ok: true; readonly data: T } | { readonly ok: false; readonly error: ApiFailure };

/** What `POST /api/auth/telegram` answers. */
export interface SessionAnswer {
  readonly token: string;
  readonly expiresAt: number;
  /**
   * Task 15: `start_param` of the launch — `t42` from a shared track, `home` from the menu — as
   * the server read it out of the launch it verified. `null` when the launch carried none, and
   * always on a refresh. Optional because a server from before task 15 says nothing.
   */
  readonly startParam?: string | null | undefined;
  readonly user: {
    readonly id: number;
    readonly firstName: string;
    readonly username?: string | undefined;
    readonly isAdmin: boolean;
  };
}

/** The message shown when the request never reached us. Russian, like everything a person reads. */
export const OFFLINE_MESSAGE = 'Нет связи с сервером. Проверьте интернет и попробуйте ещё раз.';

/** Replaceable for tests; nothing else should touch it. */
export type Fetcher = typeof fetch;

interface RequestOptions {
  readonly method?: string;
  readonly body?: unknown;
  readonly token?: string | undefined;
  readonly fetcher?: Fetcher | undefined;
  /** Task 08: let the request outlive the page — a save sent as the app is minimised or closed. */
  readonly keepalive?: boolean | undefined;
  /**
   * Task 43: `false` keeps a `401` the caller's — the retry itself, and the renewal of the pass,
   * which answers its own refusal by signing in rather than by asking itself again.
   */
  readonly renew?: boolean | undefined;
}

/**
 * Task 43: what a `401` to a request with a pass is handed to — the new pass, or `undefined`
 * when there is none to be had. Set by `store/session.ts`.
 */
export type UnauthorizedHandler = () => Promise<string | undefined>;

let unauthorized: UnauthorizedHandler | undefined;

/** Task 43: registers the one handler of an expired pass; `undefined` takes it away. */
export function onUnauthorized(handler: UnauthorizedHandler | undefined): void {
  unauthorized = handler;
}

/** One request, one result. */
export async function request<T>(
  path: string,
  options: RequestOptions = {},
): Promise<ApiResult<T>> {
  const doFetch = options.fetcher ?? fetch;

  let response: Response;
  try {
    response = await doFetch(path, {
      method: options.method ?? 'GET',
      headers: {
        // Only with a body. Fastify refuses a request that announces JSON and sends none, and
        // the hearts of task 11 are `PUT` and `DELETE` without one.
        ...(options.body !== undefined ? { 'content-type': 'application/json' } : {}),
        ...(options.token !== undefined ? { authorization: `Bearer ${options.token}` } : {}),
      },
      ...(options.body !== undefined ? { body: JSON.stringify(options.body) } : {}),
      ...(options.keepalive === true ? { keepalive: true } : {}),
    });
  } catch {
    return { ok: false, error: { code: 'OFFLINE', message: OFFLINE_MESSAGE, status: 0 } };
  }

  // Task 43: an expired pass, renewed once — see «A pass that ran out» in the header.
  if (
    response.status === 401 &&
    options.token !== undefined &&
    options.renew !== false &&
    unauthorized !== undefined
  ) {
    const fresh = await unauthorized();
    if (fresh !== undefined)
      return await request<T>(path, { ...options, token: fresh, renew: false });
  }

  if (response.ok) {
    // A 200 whose body is not JSON is a proxy page or a truncated answer. It is a failure
    // like any other, not an exception thrown past every caller.
    try {
      return { ok: true, data: (await response.json()) as T };
    } catch {
      return {
        ok: false,
        error: {
          code: 'INTERNAL',
          message: 'Что-то пошло не так. Попробуйте ещё раз.',
          status: response.status,
        },
      };
    }
  }

  // A refusal we shaped ourselves carries the envelope. Anything else — a proxy's own 502
  // page, say — still has to become one, because the screen above has exactly one way to
  // show a failure.
  try {
    const body = (await response.json()) as {
      error?: { code?: string; message?: string; details?: Record<string, unknown> };
    };
    return {
      ok: false,
      error: {
        code: body.error?.code ?? 'INTERNAL',
        message: body.error?.message ?? 'Что-то пошло не так. Попробуйте ещё раз.',
        details: body.error?.details,
        status: response.status,
      },
    };
  } catch {
    return {
      ok: false,
      error: {
        code: 'INTERNAL',
        message: 'Что-то пошло не так. Попробуйте ещё раз.',
        status: response.status,
      },
    };
  }
}

/** The launch. `initData` goes over verbatim — it is signed, and parsing it here would break that. */
export async function login(
  initData: string,
  fetcher?: Fetcher,
): Promise<ApiResult<SessionAnswer>> {
  return await request<SessionAnswer>('/api/auth/telegram', {
    method: 'POST',
    body: { initData },
    ...(fetcher !== undefined ? { fetcher } : {}),
  });
}

/**
 * Task 43: a fresh pass for a session that is still alive — `POST /api/auth/refresh`. Its `401`
 * stays here: the session's answer to it is to sign in again, not to ask for a renewal twice.
 */
export async function refreshSession(token: string): Promise<ApiResult<SessionAnswer>> {
  return await request<SessionAnswer>('/api/auth/refresh', {
    method: 'POST',
    token,
    renew: false,
  });
}

/* ────────────────────────────────────────────────────────────────────────────
 * The catalogue — task 05
 *
 * The shapes are the server's, field for field: `apps/api/src/routes/catalogue.ts` carries
 * the contract in its header, and this is the reading side of it.
 * ──────────────────────────────────────────────────────────────────────────── */

/** A riddim or a tag, as a track names it. */
export interface NamedRef {
  readonly id: number;
  readonly name: string;
}

/** One track of a list. */
export interface TrackItem {
  readonly id: number;
  readonly title: string;
  /** The whole billing. `''` when nobody is known — and then nothing is drawn for it. */
  readonly performer: string;
  /** The first name of the billing: the row this track is under in «Исполнители». */
  readonly artist: { readonly key: string; readonly name: string } | null;
  readonly year: number | null;
  readonly durationS: number;
  /** A ready URL, or `null` for the house label. */
  readonly cover: string | null;
  readonly riddim: NamedRef | null;
  readonly tags: readonly NamedRef[];
  /** Task 07: the sound, with its own credential, and when that credential stops working. */
  readonly stream: StreamLink;
  /**
   * Task 46: the beat the server measured, for the mix of two decks — `null` until measured and
   * for a track never measured. Absent from a server before task 46, which reads the same as
   * `null`. The first beat of the file is at `leadInS + beat.offsetS`.
   */
  readonly beat?: TrackBeat | null | undefined;
  /** Task 46: silence before the music and after it, seconds; absent or 0 until measured. */
  readonly leadInS?: number | undefined;
  readonly tailS?: number | undefined;
}

/** Task 46: one track's beat, as `apps/api/src/routes/catalogue.ts` hands it out. */
export interface TrackBeat {
  readonly bpm: number;
  /** The first beat, seconds after the lead-in. */
  readonly offsetS: number;
  /** 0 … 1, on the grid of eighths; the mix lays beats on beats from 0.6 up. */
  readonly confidence: number;
  /**
   * 2026-10-05: the drum pattern of one beat — 8 marks of the low bands, then 8 of the high, each
   * band summing to 1; `null` or absent before it (`apps/api/src/routes/catalogue.ts`).
   */
  readonly profile?: readonly number[] | null | undefined;
}

/** A signed sound URL. `<audio>` cannot send the session, so the URL carries its own pass. */
export interface StreamLink {
  readonly url: string;
  /** Unix seconds. */
  readonly expiresAt: number;
}

export interface TrackPage {
  readonly items: readonly TrackItem[];
  /** `null` is the end of the list. */
  readonly nextCursor: string | null;
  /** Only on the first page. */
  readonly total?: number | undefined;
}

export interface ArtistItem {
  readonly key: string;
  readonly name: string;
  readonly trackCount: number;
}

export interface RiddimItem {
  readonly id: number;
  readonly name: string;
  readonly trackCount: number;
}

/**
 * Which list: all tracks, or one artist's, riddim's or tag's — or, from task 11, the
 * listener's favourites or recent tracks, which the server answers in the same page shape.
 */
export type TrackQuery =
  | { readonly kind: 'all' }
  | { readonly kind: 'artist'; readonly key: string }
  | { readonly kind: 'riddim'; readonly id: number }
  | { readonly kind: 'tag'; readonly id: number }
  | { readonly kind: 'favorites' }
  | { readonly kind: 'recent' }
  /** Task 51: «Моё → Новое» — the catalogue's tracks posted since `since` (unix seconds). */
  | { readonly kind: 'fresh'; readonly since: number };

/** Rows asked for per page. A screenful and a half on a large phone, small on 3G. */
export const PAGE_SIZE = 100;

/**
 * The address of one page of a list: `GET /api/tracks` with the one filter, the page size and
 * the cursor — or `/api/favorites` with the same two, or `/api/recent`, which is always whole.
 */
export function tracksPath(
  query: TrackQuery,
  cursor: string | undefined,
  limit: number = PAGE_SIZE,
): string {
  if (query.kind === 'recent') return '/api/recent';
  const params = new URLSearchParams({ limit: String(limit) });
  if (query.kind === 'artist') params.set('artist', query.key);
  if (query.kind === 'riddim') params.set('riddim', String(query.id));
  if (query.kind === 'tag') params.set('tag', String(query.id));
  if (query.kind === 'fresh') params.set('since', String(query.since));
  if (cursor !== undefined) params.set('cursor', cursor);
  const base = query.kind === 'favorites' ? '/api/favorites' : '/api/tracks';
  return `${base}?${params.toString()}`;
}

export async function fetchTracks(
  token: string | undefined,
  query: TrackQuery,
  cursor?: string,
): Promise<ApiResult<TrackPage>> {
  return await request<TrackPage>(tracksPath(query, cursor), { token });
}

/**
 * Task 72: how long a list is, and nothing else — one row asked for, and the `total` every first
 * page carries (`apps/api/src/routes/catalogue.ts`, `routes/my.ts`). The numbers on «Моё»'s
 * switch; the server has no answer that is only a number, and needs none.
 */
export async function fetchListTotal(
  token: string | undefined,
  query: TrackQuery,
): Promise<ApiResult<number | undefined>> {
  const result = await request<TrackPage>(tracksPath(query, undefined, 1), { token });
  return result.ok ? { ok: true, data: result.data.total } : result;
}

export async function fetchArtists(
  token: string | undefined,
): Promise<ApiResult<{ readonly items: readonly ArtistItem[] }>> {
  return await request('/api/artists', { token });
}

export async function fetchRiddims(
  token: string | undefined,
): Promise<ApiResult<{ readonly items: readonly RiddimItem[] }>> {
  return await request('/api/riddims', { token });
}

/* ────────────────────────────────────────────────────────────────────────────
 * Search — task 10
 *
 * `apps/api/src/routes/search.ts` carries the contract in its header.
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * One clip: an mp4 from the channel or a YouTube link. The same shape in the results of
 * search and in the list of the «Видео» tab (`apps/api/src/routes/videos.ts`).
 */
export interface VideoItem {
  readonly id: number;
  readonly kind: 'video' | 'youtube';
  readonly title: string;
  /** `''` when nobody is known — and then nothing is drawn for it, as with a track. */
  readonly performer: string;
  readonly durationS: number;
  readonly cover: string | null;
  /**
   * Task 13: the video the player plays and the address «Открыть в YouTube» opens. `null` for
   * an mp4, and for a record the server could not vouch for — such a clip is not playable.
   */
  readonly youtube: YoutubeRef | null;
  /**
   * Task 14: the mp4's bytes on their own route (`/api/video/:id`), signed for six hours like a
   * track's sound. `null` for a YouTube link — YouTube's player plays it from YouTube.
   */
  readonly stream: StreamLink | null;
}

/** A YouTube video, as a clip names it: its identifier and its canonical watch address. */
export interface YoutubeRef {
  readonly id: string;
  readonly url: string;
}

/** One page of the «Видео» tab. */
export interface VideoPage {
  readonly items: readonly VideoItem[];
  /** `null` is the end of the list. */
  readonly nextCursor: string | null;
  /**
   * On the first page — and on every answer of the tab's field (task 76) and of «Избранные»
   * (task 77), each one page.
   */
  readonly total?: number | undefined;
}

/** Clips asked for per page. */
export const VIDEO_PAGE_SIZE = 50;

export function videosPath(cursor: string | undefined): string {
  const params = new URLSearchParams({ limit: String(VIDEO_PAGE_SIZE) });
  if (cursor !== undefined) params.set('cursor', cursor);
  return `/api/videos?${params.toString()}`;
}

/** A page of the «Видео» tab: `GET /api/videos`, newest post first. Task 13. */
export async function fetchVideos(
  token: string | undefined,
  cursor?: string,
): Promise<ApiResult<VideoPage>> {
  return await request<VideoPage>(videosPath(cursor), { token });
}

export function videoSearchPath(text: string): string {
  return `/api/videos?${new URLSearchParams({ q: text }).toString()}`;
}

/**
 * Task 76: the «Видео» tab's list narrowed by its field — every clip found, newest post first,
 * in one answer (`nextCursor` is `null`).
 */
export async function fetchVideoSearch(
  token: string | undefined,
  text: string,
): Promise<ApiResult<VideoPage>> {
  return await request<VideoPage>(videoSearchPath(text), { token });
}

/**
 * Task 77: the «Избранные» of the tab — the clips with the listener's heart, newest heart first,
 * in one answer (`nextCursor` is `null`).
 */
export async function fetchFavoriteVideos(
  token: string | undefined,
): Promise<ApiResult<VideoPage>> {
  return await request<VideoPage>('/api/videos?favorites=1', { token });
}

/** What `GET /api/search` answers: four groups, each possibly empty. */
export interface SearchAnswer {
  readonly tracks: readonly TrackItem[];
  /** More tracks matched than `tracks` holds: the screen asks for another word. */
  readonly moreTracks: boolean;
  readonly videos: readonly VideoItem[];
  readonly artists: readonly ArtistItem[];
  readonly riddims: readonly RiddimItem[];
}

export function searchPath(text: string): string {
  return `/api/search?${new URLSearchParams({ q: text }).toString()}`;
}

export async function fetchSearch(
  token: string | undefined,
  text: string,
): Promise<ApiResult<SearchAnswer>> {
  return await request<SearchAnswer>(searchPath(text), { token });
}

/* ────────────────────────────────────────────────────────────────────────────
 * The sound — task 07
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * A fresh sound URL for one track: `GET /api/tracks/:id/stream`. The player asks for it when
 * the one it holds is near the end of its six hours, and when a retry needs one.
 */
export async function fetchStreamLink(
  token: string | undefined,
  trackId: number,
  fetcher?: Fetcher,
): Promise<ApiResult<StreamLink>> {
  return await request<StreamLink>(`/api/tracks/${String(trackId)}/stream`, {
    token,
    ...(fetcher !== undefined ? { fetcher } : {}),
  });
}

/* ────────────────────────────────────────────────────────────────────────────
 * One track, and «Отправить в чат» — task 15
 *
 * `apps/api/src/routes/catalogue.ts` and `routes/share.ts` carry the contracts in their headers.
 * ──────────────────────────────────────────────────────────────────────────── */

/** The track a shared link names, as a row of the list: `GET /api/tracks/:id`. */
export async function fetchTrack(
  token: string | undefined,
  trackId: number,
  fetcher?: Fetcher,
): Promise<ApiResult<TrackItem>> {
  return await request<TrackItem>(`/api/tracks/${String(trackId)}`, {
    token,
    ...(fetcher !== undefined ? { fetcher } : {}),
  });
}

/** The clip a line of the weekly digest names, as a row of «Видео»: `GET /api/videos/:id` (task 16). */
export async function fetchVideo(
  token: string | undefined,
  videoId: number,
): Promise<ApiResult<VideoItem>> {
  return await request<VideoItem>(`/api/videos/${String(videoId)}`, { token });
}

/** A message Telegram keeps for this listener, for its share dialog. */
export interface PreparedShare {
  readonly id: string;
  /** Unix seconds. */
  readonly expiresAt: number;
}

/** Asks the server to prepare the message about a track: `POST /api/tracks/:id/share`. */
export async function prepareShare(
  token: string | undefined,
  trackId: number,
  fetcher?: Fetcher,
): Promise<ApiResult<PreparedShare>> {
  return await request<PreparedShare>(`/api/tracks/${String(trackId)}/share`, {
    method: 'POST',
    token,
    ...(fetcher !== undefined ? { fetcher } : {}),
  });
}

/* ────────────────────────────────────────────────────────────────────────────
 * The queue — task 08
 *
 * `apps/api/src/routes/queue.ts` carries the contract in its header; this is the reading and
 * the writing side of it.
 * ──────────────────────────────────────────────────────────────────────────── */

export type RepeatMode = 'off' | 'all' | 'one';

/**
 * Task 45: how one track goes into the next — `cut` as before, `blend` fades the end of one over
 * the start of the next, `mix` also brings the next to the tempo and beat (task 47).
 */
export type TransitionMode = 'cut' | 'blend' | 'mix';

/** What `GET /api/queue` answers: the saved queue, with only live tracks and fresh links. */
export interface QueueAnswer {
  readonly items: readonly TrackItem[];
  readonly index: number;
  readonly shuffle: boolean;
  readonly shuffleSeed: number;
  readonly shuffleAnchor: number;
  readonly repeat: RepeatMode;
  readonly positionS: number;
  readonly updatedAt: number | null;
  /** Task 44. Absent from a server before task 44. */
  readonly sourceKey?: string | null | undefined;
  readonly sourceCursor?: string | null | undefined;
  readonly pinned?: number | undefined;
  readonly freshFrom?: number | undefined;
  /** Task 45. Absent from a server before task 45. */
  readonly transition?: TransitionMode | undefined;
}

/** What `PUT /api/queue` takes. Ids only: the server reads the rows itself on the way back. */
export interface QueueSave {
  readonly trackIds: readonly number[];
  readonly index: number;
  readonly shuffle: boolean;
  readonly shuffleSeed: number;
  readonly shuffleAnchor: number;
  readonly repeat: RepeatMode;
  readonly positionS: number;
  /** Task 44: the list the queue goes on down, and the label of its next page; `null` — none. */
  readonly sourceKey: string | null;
  readonly sourceCursor: string | null;
  /** Task 44: «Играть следующим» — see `store/queue.ts`. */
  readonly pinned: number;
  /** Task 44: under shuffle, what was heard before the anchor — see `store/queue.ts`. */
  readonly freshFrom: number;
  /** Task 45: how one track goes into the next. */
  readonly transition: TransitionMode;
}

export async function fetchQueue(
  token: string | undefined,
  fetcher?: Fetcher,
): Promise<ApiResult<QueueAnswer>> {
  return await request<QueueAnswer>('/api/queue', {
    token,
    ...(fetcher !== undefined ? { fetcher } : {}),
  });
}

/**
 * Replaces the saved queue. `keepalive`, because the moment worth saving most is the one the
 * app is being minimised or closed, and a plain request may die with the page.
 */
export async function saveQueue(
  token: string | undefined,
  body: QueueSave,
  fetcher?: Fetcher,
): Promise<ApiResult<{ readonly updatedAt: number }>> {
  return await request<{ readonly updatedAt: number }>('/api/queue', {
    method: 'PUT',
    body,
    token,
    keepalive: true,
    ...(fetcher !== undefined ? { fetcher } : {}),
  });
}

/* ────────────────────────────────────────────────────────────────────────────
 * «Моё» — task 11
 *
 * `apps/api/src/routes/my.ts` carries the contract in its header. The two lists are read
 * through `tracksPath` like any other; these are the writes, and the hearts at launch.
 * ──────────────────────────────────────────────────────────────────────────── */

/** Every favourite's id, newest first: `GET /api/favorites/ids`. */
export async function fetchFavoriteIds(
  token: string | undefined,
  fetcher?: Fetcher,
): Promise<ApiResult<{ readonly ids: readonly number[] }>> {
  return await request<{ readonly ids: readonly number[] }>('/api/favorites/ids', {
    token,
    ...(fetcher !== undefined ? { fetcher } : {}),
  });
}

/** Puts the heart on (`PUT`) or takes it off (`DELETE`). Both are safe to repeat. */
export async function saveFavorite(
  token: string | undefined,
  trackId: number,
  favorite: boolean,
  fetcher?: Fetcher,
): Promise<ApiResult<{ readonly favorite: boolean }>> {
  return await request<{ readonly favorite: boolean }>(`/api/favorites/${String(trackId)}`, {
    method: favorite ? 'PUT' : 'DELETE',
    token,
    ...(fetcher !== undefined ? { fetcher } : {}),
  });
}

/* ────────────────────────────────────────────────────────────────────────────
 * Places in long tracks — task 49
 *
 * `apps/api/src/routes/positions.ts` carries the contract in its header.
 * ──────────────────────────────────────────────────────────────────────────── */

/** Track id → the second the listener stopped at: the ids asked for, or the newest 200. */
export async function fetchPositions(
  token: string | undefined,
  ids?: readonly number[],
): Promise<ApiResult<{ readonly positions: Readonly<Record<string, number>> }>> {
  const query = ids === undefined ? '' : `?ids=${ids.map(String).join(',')}`;
  return await request(`/api/positions${query}`, { token });
}

/** Where the listener stopped in a long track. Outlives the page, like the queue's save. */
export async function savePosition(
  token: string | undefined,
  trackId: number,
  positionS: number,
): Promise<ApiResult<{ readonly updatedAt: number }>> {
  return await request(`/api/positions/${String(trackId)}`, {
    method: 'PUT',
    body: { positionS },
    token,
    keepalive: true,
  });
}

/* ────────────────────────────────────────────────────────────────────────────
 * HOT CUEs — task 60
 *
 * `apps/api/src/routes/cues.ts` carries the contract in its header.
 * ──────────────────────────────────────────────────────────────────────────── */

/** The listener's eight cues of a track, pad 1 first: a second, or `null` for a pad with none. */
export async function fetchCues(
  token: string | undefined,
  trackId: number,
): Promise<ApiResult<{ readonly cues: readonly (number | null)[] }>> {
  return await request(`/api/cues/${String(trackId)}`, { token });
}

/**
 * The slots of the track this phone changed, by number (`"1"` … `"8"`): a second, or `null` to take
 * the cue away. The others are left as they are. Outlives the page, like the queue's save.
 */
export async function saveCues(
  token: string | undefined,
  trackId: number,
  cues: Readonly<Record<string, number | null>>,
): Promise<ApiResult<{ readonly updatedAt: number }>> {
  return await request(`/api/cues/${String(trackId)}`, {
    method: 'PUT',
    body: { cues },
    token,
    keepalive: true,
  });
}

/** The track has really started: it is now the newest of the listener's recent tracks. */
export async function markPlayed(
  token: string | undefined,
  trackId: number,
  fetcher?: Fetcher,
): Promise<ApiResult<{ readonly ok: true }>> {
  return await request<{ readonly ok: true }>(`/api/recent/${String(trackId)}`, {
    method: 'PUT',
    token,
    ...(fetcher !== undefined ? { fetcher } : {}),
  });
}
